<template>
  <div class="mto-indicators-panel" :class="{ 'mto-indicators-panel--embedded': embedded }">
    <h2 v-if="!embedded" class="general-indicators__section-title mb-4">Показатели МТО</h2>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

    <template v-if="!loading && data">
      <template v-if="isMasterView">
        <div class="mto-indicators-panel__block mb-6">
          <h3 class="mto-indicators-panel__subtitle">Общие показатели по компании</h3>
          <mto-indicators-entity-card
            title="Вся компания"
            :doorphones="data.company.doorphones"
            :barriers-count="data.company.barriers.count"
            :gates-count="data.company.gates.count"
            :status-description="statusDescription"
          />
        </div>

        <div
          v-if="groupedServiceAreas.length"
          class="mto-indicators-panel__block"
        >
          <h3 class="mto-indicators-panel__subtitle">Районы обслуживания и участки</h3>

          <v-card
            v-for="group in groupedServiceAreas"
            :key="`group-${group.area.id}`"
            flat
            outlined
            class="mto-indicators-panel__area-group mb-4"
          >
            <div class="mto-indicators-panel__area-group-head pa-4 pb-2">
              <div class="mto-indicators-panel__area-group-title">{{ group.area.name }}</div>
              <div class="mto-indicators-panel__area-group-meta">
                Участков: {{ group.sectors.length }}
              </div>
            </div>

            <div class="px-4 pb-3">
              <v-row dense>
                <v-col cols="12" md="5">
                  <mto-doorphone-status-chart
                    embedded
                    :doorphones-indicators="group.area.doorphones || {}"
                    :status-description="statusDescription"
                    :title="`Статусы домофонов — ${group.area.name}`"
                    empty-text="Нет домофонов в районе"
                  />
                </v-col>
                <v-col cols="12" md="7">
                  <mto-indicators-entity-card
                    title=""
                    summary-only
                    :doorphones="group.area.doorphones"
                    :barriers-count="group.area.barriers.count"
                    :gates-count="group.area.gates.count"
                    :status-description="statusDescription"
                  />
                </v-col>
              </v-row>
            </div>

            <div v-if="group.sectors.length" class="px-4 pb-4">
              <button
                type="button"
                class="mto-indicators-panel__sectors-toggle"
                @click="toggleSectors(group.area.id)"
              >
                <v-icon small class="mr-1">
                  {{ isSectorsExpanded(group.area.id) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
                Участки этого района ({{ group.sectors.length }})
              </button>
              <v-expand-transition>
                <div v-show="isSectorsExpanded(group.area.id)" class="mt-3">
                  <v-row dense>
                    <v-col
                      v-for="sector in group.sectors"
                      :key="`master-sector-${sector.id}`"
                      cols="12"
                      sm="6"
                      lg="4"
                    >
                      <mto-indicators-entity-card
                        :title="sector.name"
                        :subtitle="group.area.name"
                        badge="Участок"
                        nested
                        :link="`/mto-sector/${sector.id}`"
                        :doorphones="sector.doorphones"
                        :barriers-count="sector.barriers.count"
                        :gates-count="sector.gates.count"
                        :equipment-counts="sector.equipment_counts"
                        :status-description="statusDescription"
                      />
                    </v-col>
                  </v-row>
                </div>
              </v-expand-transition>
            </div>
          </v-card>
        </div>
      </template>

      <template v-else-if="data.service_areas && data.service_areas.length">
        <v-row>
          <v-col
            v-for="area in data.service_areas"
            :key="`area-full-${area.id}`"
            cols="12"
            xl="6"
          >
            <v-card flat outlined class="mto-indicators-panel__area-group h-100">
              <div class="mto-indicators-panel__area-group-head pa-4 pb-2">
                <div class="mto-indicators-panel__area-group-title">{{ area.name }}</div>
                <div v-if="area.master && area.master.name" class="mto-indicators-panel__area-group-meta">
                  Мастер: {{ area.master.name }}
                </div>
                <div v-else-if="area.masters && area.masters.length" class="mto-indicators-panel__area-group-meta">
                  Мастер: {{ area.masters.map(m => m.name).join(', ') }}
                </div>
              </div>

              <div class="px-4 pb-3 pt-2">
                <v-row dense>
                  <v-col cols="12" md="5">
                    <mto-doorphone-status-chart
                      embedded
                      :doorphones-indicators="area.doorphones || {}"
                      :status-description="statusDescription"
                      :title="`Статусы домофонов — ${area.name}`"
                      empty-text="Нет домофонов в районе"
                    />
                  </v-col>
                  <v-col cols="12" md="7">
                    <mto-indicators-entity-card
                      title=""
                      summary-only
                      :doorphones="area.doorphones"
                      :barriers-count="area.barriers.count"
                      :gates-count="area.gates.count"
                      :equipment-counts="area.equipment_counts"
                      :status-description="statusDescription"
                    />
                  </v-col>
                </v-row>
              </div>

              <div v-if="area.mto_sectors && area.mto_sectors.length" class="px-4 pb-4">
                <button
                  type="button"
                  class="mto-indicators-panel__sectors-toggle"
                  @click="toggleSectors(area.id)"
                >
                  <v-icon small class="mr-1">
                    {{ isSectorsExpanded(area.id) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                  </v-icon>
                  Участки МТО района ({{ area.mto_sectors.length }})
                </button>
                <v-expand-transition>
                  <div v-show="isSectorsExpanded(area.id)" class="mt-3">
                    <v-row dense>
                      <v-col
                        v-for="sector in area.mto_sectors"
                        :key="`nested-${sector.id}`"
                        cols="12"
                        md="6"
                      >
                        <mto-indicators-entity-card
                          :title="sector.name"
                          :subtitle="area.name"
                          badge="Участок"
                          nested
                          :link="`/mto-sector/${sector.id}`"
                          :masters="sector.master ? [sector.master] : sector.masters"
                          :doorphones="sector.doorphones"
                          :barriers-count="sector.barriers.count"
                          :gates-count="sector.gates.count"
                          :equipment-counts="sector.equipment_counts"
                          :status-description="statusDescription"
                        />
                      </v-col>
                    </v-row>
                  </div>
                </v-expand-transition>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <v-alert v-else type="info" outlined dense class="mb-0">
        Нет данных по участкам МТО.
      </v-alert>
    </template>
  </div>
</template>

<script>
import MtoIndicatorsEntityCard from '@/components/reports/MtoIndicatorsEntityCard';
import MtoDoorphoneStatusChart from '@/components/reports/MtoDoorphoneStatusChart';

export default {
  name: 'MtoSectorIndicatorsPanel',
  components: {
    MtoIndicatorsEntityCard,
    MtoDoorphoneStatusChart,
  },
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: null,
    },
    statusDescription: {
      type: Object,
      default: () => ({}),
    },
  },
  data () {
    return {
      expandedSectorAreas: {},
    };
  },
  computed: {
    isMasterView () {
      return !!(this.data && this.data.company);
    },
    groupedServiceAreas () {
      if (!this.data) return [];

      const areas = this.data.service_areas || [];
      const sectors = this.data.mto_sectors || [];

      return areas.map((area) => ({
        area,
        sectors: sectors.filter(sector => Number(sector.service_area_id) === Number(area.id)),
      }));
    },
  },
  methods: {
    isSectorsExpanded (areaId) {
      return !!this.expandedSectorAreas[areaId];
    },
    toggleSectors (areaId) {
      this.$set(this.expandedSectorAreas, areaId, !this.expandedSectorAreas[areaId]);
    },
  },
};
</script>

<style lang="scss" scoped>
.mto-indicators-panel {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.mto-indicators-panel--embedded {
  margin-top: 0;
  padding-top: 0;
  border-top: 0;
}

.mto-indicators-panel__subtitle {
  font-size: 1.0625rem;
  font-weight: 500;
  color: #37474f;
  margin: 0 0 12px;
}

.mto-indicators-panel__area-group {
  border-radius: 14px;
  overflow: hidden;
}

.mto-indicators-panel__area-group-head {
  background: #f7fafc;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.mto-indicators-panel__area-group-title {
  font-size: 1rem;
  font-weight: 600;
  color: #263238;
}

.mto-indicators-panel__area-group-meta {
  margin-top: 4px;
  font-size: 0.8125rem;
  color: #78909c;
  line-height: 1.4;
}

.mto-indicators-panel__sectors-toggle {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #607d8b;
}

.mto-indicators-panel__sectors-toggle:hover {
  color: #37474f;
}

@media screen and (max-width: 700px) {
  .mto-indicators-panel {
    margin-top: 20px;
    padding-top: 16px;
  }
}
</style>
